import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { EventBus } from '@/core/EventBus';
import { Position } from '@/ecs/Component';
import { Stats, Traits, Owner } from '@/ecs/components/UnitComponents';
import {
  getTraitDefinition,
  type TraitBonus,
  type TraitThreshold,
} from './TraitDefinitions';

/**
 * Trait events
 */
export const TraitEvents = {
  TRAITS_UPDATED: 'traits:updated',
  THRESHOLD_REACHED: 'traits:thresholdReached',
  THRESHOLD_LOST: 'traits:thresholdLost',
} as const;

/**
 * Active trait with current count and bonus
 */
export interface ActiveTrait {
  traitId: string;
  name: string;
  count: number;
  activeThreshold: TraitThreshold | null;
  nextThreshold: TraitThreshold | null;
}

/**
 * Cached stat modifiers from traits
 */
export interface TraitModifiers {
  attackMultiplier: number;
  defenseMultiplier: number;
  healthMultiplier: number;
  speedMultiplier: number;
  attackSpeedMultiplier: number;
  abilityPowerMultiplier: number;
  rangeBonus: number;
  critChanceBonus: number;
  lifestealPercent: number;
  damageReductionPercent: number;
}

const DEFAULT_MODIFIERS: TraitModifiers = {
  attackMultiplier: 1.0,
  defenseMultiplier: 1.0,
  healthMultiplier: 1.0,
  speedMultiplier: 1.0,
  attackSpeedMultiplier: 1.0,
  abilityPowerMultiplier: 1.0,
  rangeBonus: 0,
  critChanceBonus: 0,
  lifestealPercent: 0,
  damageReductionPercent: 0,
};

/**
 * Manages trait synergies and bonuses
 */
export class TraitSystem {
  private world: World;
  private eventBus: EventBus;

  // Cached active traits per player
  private player1Traits: Map<string, number> = new Map();
  private player2Traits: Map<string, number> = new Map();

  // Cached modifiers per player
  private player1Modifiers: TraitModifiers = { ...DEFAULT_MODIFIERS };
  private player2Modifiers: TraitModifiers = { ...DEFAULT_MODIFIERS };

  constructor(world: World, eventBus: EventBus) {
    this.world = world;
    this.eventBus = eventBus;
  }

  /**
   * Calculate traits for a player based on their active units
   */
  calculateTraits(playerId: number): void {
    const traitCounts = new Map<string, number>();

    // Get all active units for this player
    const units = this.world.query(Position, Traits, Owner).filter((entity) => {
      const owner = entity.getComponent(Owner);
      return owner && owner.playerId === playerId && !owner.isOnBench;
    });

    // Count traits
    for (const unit of units) {
      const traits = unit.getComponent(Traits)!;
      for (const traitId of traits.traitIds) {
        const current = traitCounts.get(traitId) ?? 0;
        traitCounts.set(traitId, current + 1);
      }
    }

    // Store results
    if (playerId === 1) {
      this.player1Traits = traitCounts;
    } else {
      this.player2Traits = traitCounts;
    }

    // Recalculate modifiers
    this.calculateModifiers(playerId);

    // Emit update event
    this.eventBus.emit(TraitEvents.TRAITS_UPDATED, {
      playerId,
      traits: this.getActiveTraits(playerId),
    });
  }

  /**
   * Calculate stat modifiers from active traits
   */
  private calculateModifiers(playerId: number): void {
    const modifiers: TraitModifiers = { ...DEFAULT_MODIFIERS };
    const traitCounts = playerId === 1 ? this.player1Traits : this.player2Traits;

    for (const [traitId, count] of traitCounts) {
      const definition = getTraitDefinition(traitId);
      if (!definition) continue;

      // Find highest active threshold
      let activeThreshold: TraitThreshold | null = null;
      for (const threshold of definition.thresholds) {
        if (count >= threshold.count) {
          activeThreshold = threshold;
        }
      }

      if (activeThreshold) {
        this.applyBonus(modifiers, activeThreshold.bonus);
      }
    }

    // Store modifiers
    if (playerId === 1) {
      this.player1Modifiers = modifiers;
    } else {
      this.player2Modifiers = modifiers;
    }
  }

  /**
   * Apply a trait bonus to modifiers
   */
  private applyBonus(modifiers: TraitModifiers, bonus: TraitBonus): void {
    if (bonus.attackPercent) {
      modifiers.attackMultiplier += bonus.attackPercent / 100;
    }
    if (bonus.defensePercent) {
      modifiers.defenseMultiplier += bonus.defensePercent / 100;
    }
    if (bonus.healthPercent) {
      modifiers.healthMultiplier += bonus.healthPercent / 100;
    }
    if (bonus.speedPercent) {
      modifiers.speedMultiplier += bonus.speedPercent / 100;
    }
    if (bonus.attackSpeedPercent) {
      modifiers.attackSpeedMultiplier += bonus.attackSpeedPercent / 100;
    }
    if (bonus.abilityPowerPercent) {
      modifiers.abilityPowerMultiplier += bonus.abilityPowerPercent / 100;
    }
    if (bonus.rangeBonus) {
      modifiers.rangeBonus += bonus.rangeBonus;
    }
    if (bonus.critChanceBonus) {
      modifiers.critChanceBonus += bonus.critChanceBonus;
    }
    if (bonus.lifestealPercent) {
      modifiers.lifestealPercent += bonus.lifestealPercent;
    }
    if (bonus.damageReductionPercent) {
      modifiers.damageReductionPercent += bonus.damageReductionPercent;
    }
  }

  /**
   * Get active traits for a player
   */
  getActiveTraits(playerId: number): ActiveTrait[] {
    const traitCounts = playerId === 1 ? this.player1Traits : this.player2Traits;
    const activeTraits: ActiveTrait[] = [];

    for (const [traitId, count] of traitCounts) {
      const definition = getTraitDefinition(traitId);
      if (!definition) continue;

      // Find active and next threshold
      let activeThreshold: TraitThreshold | null = null;
      let nextThreshold: TraitThreshold | null = null;

      for (let i = 0; i < definition.thresholds.length; i++) {
        const threshold = definition.thresholds[i];
        if (count >= threshold.count) {
          activeThreshold = threshold;
          nextThreshold = definition.thresholds[i + 1] ?? null;
        } else if (nextThreshold === null) {
          nextThreshold = threshold;
          break;
        }
      }

      activeTraits.push({
        traitId,
        name: definition.name,
        count,
        activeThreshold,
        nextThreshold,
      });
    }

    // Sort by count (descending), then by name
    activeTraits.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });

    return activeTraits;
  }

  /**
   * Get modifiers for a player
   */
  getModifiers(playerId: number): Readonly<TraitModifiers> {
    return playerId === 1 ? this.player1Modifiers : this.player2Modifiers;
  }

  /**
   * Apply trait modifiers to unit stats (for combat)
   * This creates effective stats without modifying base stats
   */
  getEffectiveStats(entity: Entity): {
    attack: number;
    defense: number;
    maxHealth: number;
    speed: number;
    attackSpeed: number;
    abilityPower: number;
    attackRange: number;
  } | null {
    const stats = entity.getComponent(Stats);
    const owner = entity.getComponent(Owner);

    if (!stats || !owner) return null;

    const modifiers = this.getModifiers(owner.playerId);

    return {
      attack: Math.floor(stats.attack * modifiers.attackMultiplier),
      defense: Math.floor(stats.defense * modifiers.defenseMultiplier),
      maxHealth: Math.floor(stats.maxHealth * modifiers.healthMultiplier),
      speed: Math.floor(stats.speed * modifiers.speedMultiplier),
      attackSpeed: stats.attackSpeed * modifiers.attackSpeedMultiplier,
      abilityPower: Math.floor(stats.abilityPower * modifiers.abilityPowerMultiplier),
      attackRange: stats.attackRange + modifiers.rangeBonus,
    };
  }

  /**
   * Get trait count for a player
   */
  getTraitCount(playerId: number, traitId: string): number {
    const traitCounts = playerId === 1 ? this.player1Traits : this.player2Traits;
    return traitCounts.get(traitId) ?? 0;
  }

  /**
   * Check if a trait threshold is active
   */
  isThresholdActive(playerId: number, traitId: string, thresholdCount: number): boolean {
    const count = this.getTraitCount(playerId, traitId);
    return count >= thresholdCount;
  }

  /**
   * Update traits for both players
   */
  updateAll(): void {
    this.calculateTraits(1);
    this.calculateTraits(2);
  }

  /**
   * Reset all trait caches
   */
  reset(): void {
    this.player1Traits.clear();
    this.player2Traits.clear();
    this.player1Modifiers = { ...DEFAULT_MODIFIERS };
    this.player2Modifiers = { ...DEFAULT_MODIFIERS };
  }
}
